import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  TextField,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material';
import {
  PlayArrow as TriggerIcon,
  Refresh as RefreshIcon,
  Check as SuccessIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  InfoOutlined as InfoIcon,
  Event as EventIcon,
  HistoryToggleOff as LastRunIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { createApiUrl } from '../../config/api';
import { formatScheduledDate } from '../../utils/dateUtils';

// Compute the next scheduled run time (as a local Date) from the configured
// HH:MM string. Weekend skip is honoured when auto_create_weekends is false.
function computeNextRun(scheduleTime = '23:00', skipWeekends = false) {
  if (!scheduleTime) return null;
  const [hh, mm] = scheduleTime.split(':').map((n) => parseInt(n, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return null;
  const now = new Date();
  const next = new Date();
  next.setHours(hh, mm, 0, 0);
  if (next <= now) {
    next.setDate(next.getDate() + 1);
  }
  if (skipWeekends) {
    // If the scheduler would run on a Saturday/Sunday, bump to Monday.
    while (next.getDay() === 0 || next.getDay() === 6) {
      next.setDate(next.getDate() + 1);
    }
  }
  return next;
}

function SchedulerManagement() {
  const { t, i18n } = useTranslation();
  const [config, setConfig] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [triggerDialogOpen, setTriggerDialogOpen] = useState(false);
  const [triggerDate, setTriggerDate] = useState('');

  useEffect(() => {
    loadData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      // Load config
      const configResponse = await fetch(createApiUrl('/api/scheduler/config'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (configResponse.ok) {
        const configData = await configResponse.json();
        setConfig(configData);
      }

      // Load stats
      const statsResponse = await fetch(createApiUrl('/api/scheduler/stats'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      // Load history
      const historyResponse = await fetch(createApiUrl('/api/scheduler/history?limit=20'), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (historyResponse.ok) {
        const historyData = await historyResponse.json();
        setHistory(historyData.history || []);
      }

      setError(null);
    } catch (err) {
      setError('Failed to load scheduler data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(createApiUrl('/api/scheduler/config'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(config)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save configuration');
      }

      setSuccess('Scheduler configuration saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleManualTrigger = async () => {
    try {
      setTriggering(true);
      setError(null);

      const response = await fetch(createApiUrl('/api/scheduler/trigger'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          date: triggerDate || undefined
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to trigger job creation');
      }

      setSuccess(`Successfully created ${data.jobs_created} jobs for ${data.date}`);
      setTimeout(() => setSuccess(null), 5000);
      setTriggerDialogOpen(false);
      loadData();
    } catch (err) {
      setError(err.message);
    } finally {
      setTriggering(false);
    }
  };

  if (loading || !config) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const locale = (i18n.language || 'en').startsWith('es') ? 'es-MX' : 'en-US';
  const lastRun = stats?.stats?.last_run_time ? new Date(stats.stats.last_run_time) : null;
  const nextRun = config?.enabled
    ? computeNextRun(config.schedule_time, !config.auto_create_weekends)
    : null;

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }} data-demo-target="page-scheduler-root">
      <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon color="primary" />
          {t('scheduler.title')}
          <Tooltip title={t('scheduler.helpTooltip')} arrow>
            <InfoIcon fontSize="small" color="action" sx={{ cursor: 'help' }} aria-label={t('scheduler.helpTooltip')} />
          </Tooltip>
        </Typography>
        <Box>
          <Button
            variant="contained"
            color="primary"
            startIcon={<TriggerIcon />}
            onClick={() => setTriggerDialogOpen(true)}
            sx={{ mr: 1 }}
            aria-label={t('scheduler.runNow')}
            data-demo-target="page-scheduler-run-now"
          >
            {t('scheduler.runNow')}
          </Button>
          <IconButton
            onClick={loadData}
            color="primary"
            aria-label={t('common.refresh')}
          >
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
        {t('scheduler.howItWorks')}
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* Status banner: last / next run */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <LastRunIcon color="action" fontSize="large" />
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t('scheduler.lastRun')}
                </Typography>
                <Typography variant="h6">
                  {lastRun ? lastRun.toLocaleString(locale) : t('scheduler.neverRun')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Card variant="outlined">
            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <EventIcon color={nextRun ? 'primary' : 'disabled'} fontSize="large" />
              <Box>
                <Typography variant="caption" color="textSecondary">
                  {t('scheduler.nextRun')}
                </Typography>
                <Typography variant="h6">
                  {nextRun ? nextRun.toLocaleString(locale) : t('scheduler.disabled')}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>{t('scheduler.totalRuns30d')}</Typography>
                <Typography variant="h4">{stats.stats.total_runs || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>{t('scheduler.successful')}</Typography>
                <Typography variant="h4" color="success.main">{stats.stats.successful_runs || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>{t('scheduler.failed')}</Typography>
                <Typography variant="h4" color="error.main">{stats.stats.failed_runs || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>{t('scheduler.totalJobsCreated')}</Typography>
                <Typography variant="h4">{stats.stats.total_jobs_created || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Configuration */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Scheduler Configuration</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Scheduler Enabled</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Automatically create jobs for the next day
                  </Typography>
                </Box>
              }
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Schedule Time (24-hour)"
              type="time"
              value={config.schedule_time || '23:00'}
              onChange={(e) => setConfig({ ...config, schedule_time: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="Time when scheduler runs daily (server timezone)"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label="Days Ahead"
              type="number"
              value={config.days_ahead || 1}
              onChange={(e) => setConfig({ ...config, days_ahead: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
              fullWidth
              inputProps={{ min: 1, max: 7 }}
              helperText="How many days in advance to create jobs (typically 1)"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.auto_create_weekends}
                  onChange={(e) => setConfig({ ...config, auto_create_weekends: e.target.checked })}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Create Weekend Jobs</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Include Saturdays and Sundays
                  </Typography>
                </Box>
              }
            />
          </Grid>
        </Grid>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveConfig}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : null}
          >
            {saving ? 'Saving...' : 'Save Configuration'}
          </Button>
        </Box>

        {config.last_updated && (
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            Last updated: {new Date(config.last_updated).toLocaleString()}
            {config.updated_by && ` by ${config.updated_by}`}
          </Typography>
        )}
      </Paper>

      {/* Scheduler History */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Recent Scheduler Runs</Typography>
        <Divider sx={{ mb: 2 }} />
        
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Status</TableCell>
                <TableCell>Run Time</TableCell>
                <TableCell>Target Date</TableCell>
                <TableCell align="right">Jobs Created</TableCell>
                <TableCell>Triggered By</TableCell>
                <TableCell>Error</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>
                    {run.success ? (
                      <Chip icon={<SuccessIcon />} label="Success" color="success" size="small" />
                    ) : (
                      <Chip icon={<ErrorIcon />} label="Failed" color="error" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{new Date(run.run_time).toLocaleString()}</TableCell>
                  <TableCell>{formatScheduledDate(run.target_date)}</TableCell>
                  <TableCell align="right">{run.jobs_created}</TableCell>
                  <TableCell>{run.triggered_by || <em>Automatic</em>}</TableCell>
                  <TableCell>
                    {run.error_message && (
                      <Tooltip title={run.error_message}>
                        <Typography variant="caption" color="error" sx={{ cursor: 'pointer' }}>
                          {run.error_message.substring(0, 50)}...
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {history.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">No scheduler history yet</Typography>
          </Box>
        )}
      </Paper>

      {/* Manual Trigger Dialog */}
      <Dialog open={triggerDialogOpen} onClose={() => setTriggerDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manually Trigger Job Creation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Create active jobs for a specific date. Leave blank to create jobs for tomorrow.
          </Typography>
          <TextField
            label="Target Date"
            type="date"
            value={triggerDate}
            onChange={(e) => setTriggerDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            helperText="Jobs will be created from job_sites for this date"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTriggerDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleManualTrigger}
            disabled={triggering}
            startIcon={triggering ? <CircularProgress size={20} /> : <TriggerIcon />}
          >
            {triggering ? 'Creating...' : 'Create Jobs'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SchedulerManagement;
